import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Star, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

const schema = z.object({
  author: z.string().trim().min(1, "Please enter your name").max(100),
  author_role: z.string().trim().max(100).optional(),
  quote: z.string().trim().min(5, "Tell us a little more").max(1000),
  rating: z.number().int().min(1).max(5),
});

export const Route = createFileRoute("/review")({
  head: () => ({
    meta: [
      { title: "Leave a Review | Creed Car Rental Miami" },
      { name: "description", content: "Rented a luxury car with Creed in Miami? Share your experience and help other travelers find the right exotic or SUV rental." },
      { property: "og:title", content: "Leave a Review | Creed Car Rental Miami" },
      { property: "og:description", content: "Share your experience renting with Creed Car Rental in Miami." },
      { property: "og:url", content: "https://creedcarental.com/review" },
    ],
    links: [{ rel: "canonical", href: "https://creedcarental.com/review" }],
  }),
  component: ReviewPage,
});

function ReviewPage() {
  const [author, setAuthor] = useState("");
  const [authorRole, setAuthorRole] = useState("");
  const [quote, setQuote] = useState("");
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({ author, author_role: authorRole || undefined, quote, rating });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Please check the form");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("testimonials").insert({
      author: parsed.data.author,
      author_role: parsed.data.author_role ?? null,
      quote: parsed.data.quote,
      rating: parsed.data.rating,
      active: false,
      approved: false,
      submitted_via: "public",
      sort_order: 999,
    });
    setSubmitting(false);
    if (error) { toast.error(error.message); return; }
    setDone(true);
  };

  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />

      {/* Hero header */}
      <section className="relative isolate overflow-hidden border-b border-border/60 bg-espresso py-16 md:py-20">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(60% 80% at 85% 20%, oklch(0.72 0.15 65 / 0.35), transparent 60%), radial-gradient(50% 70% at 10% 90%, oklch(0.72 0.15 65 / 0.18), transparent 60%)",
          }}
          aria-hidden
        />
        <svg
          className="pointer-events-none absolute -right-24 -top-24 h-[520px] w-[520px] text-accent/20"
          viewBox="0 0 600 600"
          fill="none"
          aria-hidden
        >
          <circle cx="300" cy="300" r="220" stroke="currentColor" strokeWidth="1" />
          <circle cx="300" cy="300" r="160" stroke="currentColor" strokeWidth="1" />
          <circle cx="300" cy="300" r="100" stroke="currentColor" strokeWidth="1" />
        </svg>
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <h1 className="font-display text-4xl text-cream md:text-5xl">Leave a review</h1>
          <p className="mt-3 text-cream/70">Rented with us? We'd love to hear about your trip.</p>
        </div>
      </section>

      <div className="mx-auto max-w-2xl px-6 py-16">
        {done ? (
          <Card className="p-10 text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-accent" />
            <h1 className="mt-6 font-display text-3xl text-espresso">Thank you!</h1>
            <p className="mt-3 text-muted-foreground">Your review has been submitted and will appear on our site once we've had a chance to review it.</p>
            <Button asChild className="mt-8 bg-primary"><Link to="/">Back to home</Link></Button>
          </Card>
        ) : (
          <>
            <Card className="mt-8 space-y-5 p-6">
              <form onSubmit={submit} className="space-y-5">
                <div className="space-y-2">
                  <Label>Your name</Label>
                  <Input value={author} onChange={e => setAuthor(e.target.value)} placeholder="Jane D." maxLength={100} required />
                </div>
                <div className="space-y-2">
                  <Label>Where you're from <span className="text-muted-foreground">(optional)</span></Label>
                  <Input value={authorRole} onChange={e => setAuthorRole(e.target.value)} placeholder="Visited from NYC" maxLength={100} />
                </div>
                <div className="space-y-2">
                  <Label>Rating</Label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setRating(n)}
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(0)}
                        className="p-1"
                        aria-label={`Rate ${n} star${n === 1 ? "" : "s"}`}
                        aria-pressed={rating === n}
                      >
                        <Star className={`h-8 w-8 transition ${(hover || rating) >= n ? "fill-accent text-accent" : "text-muted-foreground/30"}`} />
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Your review</Label>
                  <Textarea rows={5} value={quote} onChange={e => setQuote(e.target.value)} placeholder="The car was spotless, pickup was a breeze…" maxLength={1000} required />
                  <div className="text-right text-xs text-muted-foreground">{quote.length}/1000</div>
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-primary">
                  {submitting ? "Submitting…" : "Submit review"}
                </Button>
              </form>
            </Card>
          </>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
